import { defineComponent, h, PropType } from 'vue';
import { coming_soon } from '@components/icons';
import { State } from '../../state';
import {
    uil_delete_selected_objects,
    uil_get_body_of_edge,
    uil_get_body_of_face,
    uil_get_faces_of_edge,
    uil_is_object_fixed,
    uil_is_object_visible,
} from '../../ui_logic';
import BarButton from '@components/bar-button';
import { unique } from '@vaaas/fpts/array';
import type { Body } from '@phi/jcore';
import { shift_pressed } from '../../keypress';
import invisible_svg from '../../../assets/img/SVG_icons/Top/create_model/invisible.svg';
import { img } from '@phi/components';
import { uid_fill_all_connected_component_invisible_faces } from '@frontend/ui_display';
import InvisibleTooltip from './InvisibleTooltip';
import { ISubscriptions } from '@phi/subscriptions/frontend';

export const InvisibleIcon = invisible_svg;

export default defineComponent({
    props: {
        state: { required: true, type: Object as PropType<State> },
        subscriptions: { required: true, type: Object as PropType<ISubscriptions> },
    },

    computed: {
        coming_soon() {
            return !this.state.loaded;
        },

        alt() {
            return 'Make faces invisible';
        },

        disabled() {
            return !this.state.loaded
                || this.state.editing_reference_sketches
                || !(this.can_fill || this.can_hide);
        },

        all_faces_unfixed() {
            this.state.selected_faces_count;
            return !(uid_selected_faces.keys().some(uil_is_object_fixed));
        },

        selected_bodies() {
            this.state.selected_faces_count;
            return unique([
                ...uid_selected_faces.keys().map(uil_get_body_of_face),
                ...uid_selected_edges.keys().map(uil_get_body_of_edge),
            ]);
        },

        can_hide() {
            return this.state.selected_faces_count > 0
                && this.selected_bodies.length === 1 // all faces belong in the same body
                && this.visible_faces_of_body(this.selected_bodies[0]!) // not all faces of the body have been selected
                    .some(fid => !uid_selected_faces.hasItem(fid))
                && this.all_faces_unfixed
                && uid_raynode === uid_objects_raynode;
        },

        can_fill() {
            return this.state.selected_edges_count > 0
                && uid_selected_edges.keys() // some face of edge is hidden
                    .flatMap(uil_get_faces_of_edge)
                    .some(x => !uil_is_object_visible(x))
                && this.selected_bodies.length === 1 // all edges belong in the same body
                && uid_raynode === uid_objects_raynode;
        },
    },

    watch: {
        disabled(value: boolean) {
            this.$emit('update:disabled', value);
        },
    },

    emits: {
        'update:disabled': (x: boolean) => true,
    },

    methods: {
        onClick() {
            if (this.disabled)
                return;
            else if (this.can_hide)
                uil_delete_selected_objects(shift_pressed());
            else if (this.can_fill)
                uid_fill_all_connected_component_invisible_faces();
        },

        visible_faces_of_body(x: Body) {
            return uid_faces.keys()
                .filter(fid =>
                    uil_is_object_visible(fid)
                    && uil_get_body_of_face(fid) === x
                );
        },
    },

    render() {
        return h(BarButton, {
            id: 'invisible-button',
            onClick: this.onClick,
            disabled: this.disabled,
            selected: this.state.tut_stage === 6,
            active: this.can_fill,
            help: 'make_invisible',
        }, {
            icon: () => this.coming_soon ? coming_soon(this.alt) : img(invisible_svg, this.alt),
            tooltip: InvisibleTooltip,
        });
    },
});
